import { css } from 'styled-components';

import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';
import { PARALLAX_MODIFIER } from '~/utilities/helpers/getParallaxConfig';

export const BaseStyledStyling = {
  /**
   * Heading styling for titles and headings.
   *
   * The base declarations are the G-Star treatment (shared by Outlet and
   * Employeeshop). `font-family` already varies per brand via the
   * `--font-heading` CSS variable set in `globalStyle`. Brands that diverge
   * override the relevant properties through the `siteType` switch below — it
   * comes last so a brand's overrides win the cascade.
   */
  HeadingStyling: css`
    font-family: var(--font-heading);
    text-transform: uppercase;

    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        /* Gstar, Outlet, Employeeshop */
        default: css`
          font-weight: ${theme.variables.font.weight.bold};
          line-height: 0.95;
          letter-spacing: 0.02em;
        `,
        [SiteType.Wahts]: css`
          font-weight: ${theme.variables.font.weight.regular};
          line-height: 1;
          letter-spacing: 0.18em;
        `,
      })}
  `,
  BoxSizingBorder: css`
    &,
    *,
    ::before,
    ::after {
      box-sizing: border-box;
    }
  `,
  /**
   * Add to any element with `overflow: hidden` that can contain -- or be -- a
   * ContentBlock.
   *
   * `hidden` makes an element a scroll container, and a scroll-driven `view()`
   * timeline binds to the nearest ancestor scroll container -- one that never
   * scrolls freezes the animation. `clip` clips identically without becoming one.
   *
   * Both selectors are needed because layouts style the block itself as often as
   * they style a wrapper, and `:has()` looks at descendants only.
   *
   * ```css
   * &:has(.contentBlock--parallax),
   * &.contentBlock--parallax {
   *   overflow: clip;
   * }
   * ```
   */
  ParallaxSafeOverflow: css`
    @supports (animation-timeline: view()) {
      &:has(.${PARALLAX_MODIFIER}),
      &.${PARALLAX_MODIFIER} {
        overflow: clip;
      }
    }
  `,
  /**
   * The other half of `ParallaxSafeOverflow`, for the scroll containers that cannot be
   * talked out of being one -- a horizontal carousel needs its `overflow-x: auto`.
   *
   * A view timeline binds to the nearest ancestor scroll container of its subject, and
   * a carousel has no vertical scroll extent at all, so progress never advances: the pan
   * stops dead at whatever fraction the block happened to resolve to. Measured on the
   * stories carousel, that is 0.55 -- a crop no one chose, held forever. Off is the
   * better failure: the image keeps the position it was authored with.
   *
   * The zoom goes with it. It exists only to manufacture crop for a pan to spend, so
   * without one it is a tighter, softer image bought for nothing.
   *
   * Add to the element that declares the scrolling overflow, not to the blocks inside.
   *
   * ```css
   * .contentBlock--parallax {
   *   --parallax-zoom: 100%;
   *   .contentBlock-picture-image, .contentBlock-vimeoContainer { animation-name: none; }
   * }
   * ```
   */
  ParallaxOffInScroller: css`
    @supports (animation-timeline: view()) {
      .${PARALLAX_MODIFIER} {
        --parallax-zoom: 100%;

        .contentBlock-picture-image,
        .contentBlock-vimeoContainer {
          animation-name: none;
        }
      }
    }
  `,
};

export const { HeadingStyling, BoxSizingBorder, ParallaxSafeOverflow, ParallaxOffInScroller } =
  BaseStyledStyling;
